import { CommonModule } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, OnInit, TemplateRef, ViewChild, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCamera, faCheck, faClock, faFan, faHouse, faLightbulb, faPlug, faPlus, faPowerOff, faRotateRight, faShareNodes, faSnowflake, faTrash, faTv, faWater, faXmark } from '@fortawesome/free-solid-svg-icons';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, firstValueFrom, timeout } from 'rxjs';
import type { IScannerControls } from '@zxing/browser';
import { DeviceAccessRequest, DeviceAssociation, DeviceOperationMode, DevicePremise, SmartDevice, parseDeviceModes, parseDeviceQr } from '@app/_models/smart-device';
import { AuthenticationService } from '@app/_services/authentication.service';
import { DEVICE_REQUEST_CONTEXT, DeviceService } from '@app/_services/device.service';

type DeviceDialog = 'choice' | 'entry' | 'scan' | 'setup' | 'existing' | 'manage' | 'share' | 'delay' | 'reauth';

@Component({
  selector: 'app-smart-devices',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, RouterLink],
  templateUrl: './smart-devices.component.html',
  styleUrls: ['./smart-devices.component.css']
})
export class SmartDevicesComponent implements OnInit {
  private service = inject(DeviceService);
  private auth = inject(AuthenticationService);
  private toastr = inject(ToastrService);
  private modals = inject(NgbModal);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private modal?: NgbModalRef;
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private scanner?: IScannerControls;
  private scanGeneration = 0;

  @ViewChild('deviceDialog', { static: true }) dialogTemplate!: TemplateRef<unknown>;
  @ViewChild('scannerVideo') scannerVideo?: ElementRef<HTMLVideoElement>;

  icons = { add: faPlus, refresh: faRotateRight, home: faHouse, share: faShareNodes, delay: faClock, power: faPowerOff, close: faXmark, camera: faCamera, approve: faCheck, delete: faTrash };
  devices = signal<SmartDevice[]>([]);
  hasPending = computed(() => this.devices().some(device => device.pending));
  loading = signal(true);
  loadingPremises = signal(false);
  loadError = signal('');
  busy = signal(false);
  modalError = signal('');
  modalMessage = signal('');
  dialog = signal<DeviceDialog>('choice');
  addMode = signal<'new' | 'existing' | 'manage'>('new');
  entryMode = signal<'manual' | 'scan'>('manual');
  shareMode = signal<'qr' | 'email'>('qr');
  step = signal(1);
  premises = signal<DevicePremise[]>([]);
  requests = signal<DeviceAccessRequest[]>([]);
  requestsLoading = signal(false);
  selectedPremiseIndex = signal(0);
  selectedDevice = signal<SmartDevice | null>(null);
  qrImage = signal('');
  cameraRunning = signal(false);
  profile = signal({ firstName: '', lastName: '', email: '' });
  deviceTypes = ['light', 'fan', 'plug', 'pump', 'other'];
  dialogTitles: Record<DeviceDialog, string> = {
    choice: 'Add Device', entry: 'Device Details', scan: 'Scan QR Code', setup: 'Add Device', existing: 'Request Device Access',
    manage: 'Manage Device Requests', share: 'Share Device', delay: 'Set Operation Time', reauth: 'Session Expired'
  };
  modes: { value: DeviceOperationMode; label: string }[] = [
    { value: 'on/off', label: 'On / Off' }, { value: 'toggle', label: 'Toggle' }, { value: 'delay', label: 'Delay' }
  ];
  setupFields = [
    { key: 'passcode', label: 'Passcode', type: 'text' },
    { key: 'label', label: 'Label', type: 'text' },
    { key: 'serial', label: 'Device Serial', type: 'text' },
    { key: 'password', label: 'Device Password (optional)', type: 'password' }
  ];
  setupForm = this.fb.group({
    premise: ['', Validators.required], model: ['', Validators.required], deviceType: ['', Validators.required],
    channel: ['', [Validators.required, Validators.pattern(/^[1-9]\d*$/)]],
    passcode: ['', Validators.required], label: ['', Validators.required], serial: ['', Validators.required], password: [''],
    onOff: [false], toggle: [false], delay: [false]
  });
  existingForm = this.fb.group({ deviceId: ['', Validators.required], label: ['', Validators.required] });
  shareForm = this.fb.group({ email: ['', [Validators.required, Validators.pattern(/^[^@\s]+@[^@\s]+\.[^@\s]+$/), Validators.maxLength(254)]] });
  delayForm = this.fb.group({ preset: ['5'], minutes: [''] });
  qrForm = this.fb.group({ raw: ['', Validators.required] });
  reauthForm = this.fb.group({ username: ['', Validators.required], password: ['', Validators.required] });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.stopScanner();
      this.timers.forEach(timer => clearTimeout(timer));
      this.modal?.dismiss();
    });
  }

  ngOnInit(): void {
    void this.fetchDevices();
    void this.loadProfile();
  }

  private request<Result>(source: Observable<Result>): Promise<Result> {
    return firstValueFrom(source.pipe(timeout(30000), takeUntilDestroyed(this.destroyRef)));
  }

  async fetchDevices(): Promise<void> {
    if (this.devices().some(device => device.pending)) {
      return;
    }
    this.loading.set(true);
    this.loadError.set('');
    try {
      const associations = await this.request(this.service.getSmartDevices());
      const previous = new Map(this.devices().map(device => [device.id, device]));
      const devices = associations.map(association => this.mapAssociation(association, previous.get(String(association.id))));
      const ids = new Set(devices.map(device => device.id));
      for (const id of this.timers.keys()) {
        if (!ids.has(id)) {
          this.clearTimer(id);
        }
      }
      this.devices.set(devices);
    } catch (error) {
      this.loadError.set(this.errorMessage(error, 'Unable to load devices. Please try again.'));
      this.handleUnauthorized(error);
    } finally {
      this.loading.set(false);
    }
  }

  private mapAssociation(association: DeviceAssociation, previous?: SmartDevice): SmartDevice {
    const details = association.deviceDetails ?? {};
    return {
      id: String(association.id), propertyId: Number(association.propertyId), deviceId: String(association.deviceId ?? ''),
      deviceType: details.device_type ?? '', channel: Number(details.channel ?? 0), label: details.label || association.deviceId,
      createdBy: details.createdby ?? details.created_by ?? details.createdBy ?? association.createdby ?? association.created_by ?? association.createdBy ?? '',
      icon: details.icon ?? 'Plug', modes: parseDeviceModes(details.operation_modes ?? association.operation_modes),
      isOn: previous?.isOn ?? false, pending: false, error: ''
    };
  }

  deviceIcon(device: SmartDevice) {
    const icons: Record<string, typeof faPlug> = {
      Lightbulb: faLightbulb, Fan: faFan, Plug: faPlug, Snowflake: faSnowflake, Tv: faTv, WashingMachine: faWater, Cable: faPlug
    };
    return icons[device.icon] ?? faPlug;
  }

  private updateDevice(id: string, changes: Partial<SmartDevice>): void {
    this.devices.update(devices => devices.map(device => device.id === id ? { ...device, ...changes } : device));
  }

  async operate(device: SmartDevice, mode: DeviceOperationMode, duration = 0): Promise<boolean> {
    const current = this.devices().find(item => item.id === device.id);
    if (!current || current.pending || !current.modes.includes(mode)) {
      return false;
    }
    if (mode === 'delay' && (!Number.isSafeInteger(duration) || duration <= 0)) {
      return false;
    }
    const nextIsOn = mode === 'delay' ? true : !current.isOn;
    this.clearTimer(current.id);
    this.updateDevice(current.id, { pending: true, isOn: nextIsOn, error: '' });
    try {
      await this.sendOperation(current, mode === 'on/off' ? 'ON/OFF' : mode.toUpperCase(), mode === 'toggle' ? 5 : duration,
        mode === 'on/off' ? (nextIsOn ? 'ON' : 'OFF') : undefined);
      if (nextIsOn && mode !== 'on/off') {
        this.scheduleAutoOff(current.id, mode === 'toggle' ? 3 : duration);
      }
      this.toastr.success(mode === 'delay' ? 'Delay operation scheduled successfully.' : `${current.label} is now ${nextIsOn ? 'On' : 'Off'}.`);
      return true;
    } catch (error) {
      this.updateDevice(current.id, { isOn: current.isOn, error: this.errorMessage(error, 'Unable to operate device. Please try again.') });
      this.handleUnauthorized(error);
      return false;
    } finally {
      this.updateDevice(current.id, { pending: false });
    }
  }

  private sendOperation(device: SmartDevice, operation: string, seconds = 0, status?: string) {
    return this.request(this.service.operateSmartDevice({
      property_id: device.propertyId, device_id: device.deviceId, operation_type: operation,
      ...(status ? { status } : {}), usage_duration: seconds, operation_time: seconds
    }));
  }

  private clearTimer(id: string): void {
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
  }

  private scheduleAutoOff(id: string, seconds: number): void {
    this.clearTimer(id);
    const deadline = Date.now() + seconds * 1000;
    const schedule = () => {
      const remaining = deadline - Date.now();
      this.timers.set(id, setTimeout(() => {
        if (deadline > Date.now()) {
          schedule();
        } else {
          this.timers.delete(id);
          void this.autoOff(id);
        }
      }, Math.min(Math.max(remaining, 0), 2147483647)));
    };
    schedule();
  }

  private async autoOff(id: string): Promise<void> {
    const device = this.devices().find(item => item.id === id);
    if (!device?.isOn || this.destroyRef.destroyed) {
      return;
    }
    this.updateDevice(id, { isOn: false, pending: true });
    try {
      await this.sendOperation(device, 'ON/OFF', 0, 'OFF');
    } catch (error) {
      this.updateDevice(id, { isOn: true, error: this.errorMessage(error, 'Unable to turn device off. Please try again.') });
      this.handleUnauthorized(error);
    } finally {
      this.updateDevice(id, { pending: false });
    }
  }

  toggle(device: SmartDevice, input: HTMLInputElement): void {
    input.checked = device.isOn;
    void this.operate(device, 'toggle');
  }

  openDialog(kind: DeviceDialog): void {
    this.stopScanner();
    this.modalError.set('');
    this.modalMessage.set('');
    this.dialog.set(kind);
    if (!this.modal) {
      const modal = this.modals.open(this.dialogTemplate, { size: 'lg', centered: true, backdrop: 'static', ariaLabelledBy: 'smart-device-dialog-title', beforeDismiss: () => !this.busy() });
      this.modal = modal;
      modal.result.then(() => this.modalClosed(), () => this.modalClosed());
    }
  }

  private modalClosed(): void {
    this.stopScanner();
    this.modal = undefined;
    this.selectedDevice.set(null);
  }

  closeDialog(): void {
    if (!this.busy()) {
      this.stopScanner();
      this.modal?.close();
    }
  }

  openDelay(device: SmartDevice): void {
    if (device.pending) {
      return;
    }
    this.selectedDevice.set(device);
    this.delayForm.reset({ preset: '5', minutes: '' });
    this.openDialog('delay');
  }

  async submitDelay(): Promise<void> {
    if (this.busy()) {
      return;
    }
    const value = this.delayForm.getRawValue();
    const seconds = Math.round(Number(value.preset === 'custom' ? value.minutes : value.preset) * 60);
    if (!Number.isSafeInteger(seconds) || seconds <= 0 || !Number.isSafeInteger(Date.now() + seconds * 1000)) {
      this.modalError.set('Select a preset or enter valid custom minutes greater than 0.');
      return;
    }
    const device = this.selectedDevice();
    if (!device) {
      return;
    }
    this.busy.set(true);
    const success = await this.operate(device, 'delay', seconds);
    this.busy.set(false);
    if (success) {
      this.closeDialog();
    } else if (this.dialog() === 'delay') {
      this.modalError.set(this.devices().find(item => item.id === device.id)?.error || 'Unable to submit delay operation.');
    }
  }

  canShare(device: SmartDevice): boolean {
    const email = (value: string) => (value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || value.trim()).toLowerCase();
    const owner = email(device.createdBy);
    return !!owner && owner === email(this.profile().email);
  }

  async openShare(device: SmartDevice): Promise<void> {
    if (!this.canShare(device)) {
      return;
    }
    this.selectedDevice.set(device);
    this.shareMode.set('qr');
    this.shareForm.reset();
    this.qrImage.set('');
    this.openDialog('share');
    try {
      const { default: qr } = await import('qrcode');
      const image = await qr.toDataURL(`DI:${device.deviceId};DL:${device.label};DT:${device.deviceType};CH:${device.channel}`, { width: 260, margin: 2 });
      if (this.selectedDevice()?.id === device.id && this.dialog() === 'share') {
        this.qrImage.set(image);
      }
    } catch (error) {
      this.modalError.set(this.errorMessage(error, 'Unable to generate QR code.'));
    }
  }

  async shareByEmail(): Promise<void> {
    if (this.busy()) {
      return;
    }
    this.shareForm.controls.email.setValue((this.shareForm.controls.email.value || '').trim().toLowerCase());
    this.shareForm.markAllAsTouched();
    const device = this.selectedDevice();
    if (this.busy() || this.shareForm.invalid || !device || !this.canShare(device)) {
      return;
    }
    await this.submit(async () => {
      const response = await this.request(this.service.shareSmartDevice(device.deviceId, this.shareForm.value.email!.trim().toLowerCase()));
      this.modalMessage.set(response.message || 'Device shared successfully.');
    });
  }

  private async loadProfile(): Promise<void> {
    const user = this.auth.currentUserValue;
    this.profile.set({ firstName: '', lastName: '', email: user?.email || '' });
    if (!user?.username) {
      return;
    }
    try {
      const profile = await this.request(this.service.getSmartDeviceProfile(user.username));
      const names = (profile.fullname || '').trim().split(/\s+/);
      this.profile.set({ firstName: names[0] || '', lastName: names.slice(1).join(' '), email: profile.email?.trim() || user.email || '' });
    } catch (error) {
      this.handleUnauthorized(error);
    }
  }

  async openAdd(): Promise<void> {
    if (this.loadingPremises()) {
      return;
    }
    this.loadingPremises.set(true);
    try {
      const username = this.auth.currentUserValue?.username || '';
      const subscriptions = await this.request(this.service.getSmartDevicePremises(username));
      let access: { property_id: number; unit_number: number }[] = [];
      try {
        access = await this.request(this.service.getSmartDeviceAccess(username));
      } catch (error) {
        if ((error as { status?: number })?.status === 401) {
          throw error;
        }
      }
      const premises = subscriptions.flatMap(property => {
        const units = [...new Set(access.filter(row => Number(row.property_id) === Number(property.id)).map(row => Number(row.unit_number)).filter(Number.isInteger))];
        return (units.length ? units : [0]).map(unit => ({ propertyId: Number(property.id), unitNumber: unit,
          displayName: `${property.name || 'Property ' + property.id} - ${property.property_type === 'strata' ? 'APT' : 'LOT'} ${unit}` }));
      });
      this.premises.set(premises);
      if (!premises.length) {
        this.toastr.info('You are not associated with any property.', 'No Associations');
        return;
      }
      await this.loadProfile();
      if (this.dialog() === 'reauth' && this.modal) {
        return;
      }
      this.addMode.set('new');
      this.openDialog('choice');
    } catch (error) {
      if (!this.handleUnauthorized(error)) {
        this.toastr.error(this.errorMessage(error, 'Unable to load property associations. Please try again.'));
      }
    } finally {
      this.loadingPremises.set(false);
    }
  }

  chooseAddMode(): void {
    this.selectedPremiseIndex.set(0);
    if (this.addMode() === 'manage') {
      this.openDialog('manage');
      void this.loadRequests();
    } else {
      this.entryMode.set('manual');
      this.openDialog('entry');
    }
  }

  chooseEntry(): void {
    this.setupForm.reset({ premise: this.premises().length === 1 ? '0' : '', onOff: false, toggle: false, delay: false });
    this.existingForm.reset();
    this.step.set(1);
    if (this.entryMode() === 'scan') {
      this.qrForm.reset();
      this.openDialog('scan');
    } else {
      this.openDialog(this.addMode() === 'new' ? 'setup' : 'existing');
    }
  }

  selectedModes(): DeviceOperationMode[] {
    const values = this.setupForm.getRawValue();
    return this.modes.filter(mode => mode.value === 'on/off' ? values.onOff : mode.value === 'toggle' ? values.toggle : values.delay).map(mode => mode.value);
  }

  nextSetupStep(): void {
    const keys = this.step() === 1 ? ['premise', 'model', 'deviceType', 'channel'] : ['passcode', 'label', 'serial'];
    for (const key of keys) {
      this.setupForm.get(key)?.markAsTouched();
    }
    if (keys.some(key => this.setupForm.get(key)?.invalid || String(this.setupForm.get(key)?.value ?? '').trim() === '') || (this.step() === 1 && !this.selectedModes().length)) {
      this.modalError.set('Complete all required fields and select at least one operation mode.');
      return;
    }
    this.modalError.set('');
    this.step.update(step => Math.min(step + 1, 3));
  }

  setupPremise(): DevicePremise | undefined {
    const index = this.setupForm.value.premise;
    return index === '' || index == null ? undefined : this.premises()[Number(index)];
  }

  async submitSetup(): Promise<void> {
    this.setupForm.markAllAsTouched();
    const premise = this.setupPremise();
    const values = this.setupForm.getRawValue();
    if (this.busy() || this.setupForm.invalid || !premise || !this.selectedModes().length) {
      this.modalError.set('Complete all required device details and operation modes.');
      return;
    }
    await this.submit(async () => {
      await this.request(this.service.createSmartDevice({ propertyId: premise.propertyId, unit_number: premise.unitNumber,
        firstName: this.profile().firstName, lastName: this.profile().lastName, model: values.model!.trim(), device_type: values.deviceType,
        channel: Number(values.channel), passcode: values.passcode!.trim(), label: values.label!.trim(), device_serial: values.serial!.trim(),
        device_password: values.password || '', operation_modes: this.selectedModes() }));
      this.toastr.success('Device added successfully.');
      this.busy.set(false);
      this.closeDialog();
      await this.fetchDevices();
    });
  }

  async submitExisting(): Promise<void> {
    this.existingForm.markAllAsTouched();
    const premise = this.premises()[this.selectedPremiseIndex()];
    const values = this.existingForm.getRawValue();
    if (this.busy() || this.existingForm.invalid || !premise || !this.profile().email || !values.deviceId?.trim() || !values.label?.trim()) {
      this.modalError.set('A device ID, device label and your email are required.');
      return;
    }
    await this.submit(async () => {
      await this.request(this.service.requestSmartDeviceAccess({ propertyId: premise.propertyId, unit_number: premise.unitNumber,
        ...this.profile(), deviceId: values.deviceId!.trim(), device_label: values.label!.trim(), enabled: false }));
      this.toastr.success('Access request recorded.');
      this.busy.set(false);
      this.closeDialog();
    });
  }

  async loadRequests(): Promise<void> {
    const premise = this.premises()[this.selectedPremiseIndex()];
    if (!premise) {
      return;
    }
    this.requestsLoading.set(true);
    this.modalError.set('');
    try {
      this.requests.set(await this.request(this.service.getSmartDeviceRequests(premise.propertyId, premise.unitNumber)));
    } catch (error) {
      this.requests.set([]);
      this.modalError.set(this.errorMessage(error, 'Unable to load device access requests.'));
      this.handleUnauthorized(error);
    } finally {
      this.requestsLoading.set(false);
    }
  }

  async requestAction(request: DeviceAccessRequest, action: 'approve' | 'delete'): Promise<void> {
    if (this.busy() || this.requestsLoading() || (action === 'approve' && request.approved)) {
      return;
    }
    await this.submit(async () => {
      await this.request(action === 'approve' ? this.service.approveSmartDeviceRequest(request.id) : this.service.deleteSmartDeviceRequest(request.id));
      this.toastr.success(action === 'approve' ? 'Request approved successfully.' : 'Request deleted successfully.');
      await this.loadRequests();
      if (action === 'approve') {
        await this.fetchDevices();
      }
    });
  }

  applyQr(raw = this.qrForm.value.raw || ''): boolean {
    const data = parseDeviceQr(raw);
    if (this.addMode() === 'new') {
      if (![data['DM']?.[0], data['DT']?.[0], data['DC']?.[0], data['DL']?.[0], data['DS']?.[0]].every(Boolean)) {
        this.modalError.set('Scanned QR code does not contain valid new-device setup data.');
        return false;
      }
      this.setupForm.patchValue({ model: data['DM'][0], deviceType: data['DT'][0], channel: data['DC'][0], passcode: data['DC']?.[1] || '',
        label: data['DL'][0], serial: data['DS'][0], password: data['DP']?.[0] || '' });
      this.openDialog('setup');
    } else {
      if (!data['DI']?.[0] || !data['DL']?.[0]) {
        this.modalError.set('Scanned QR code does not contain a valid device ID and device label.');
        return false;
      }
      this.existingForm.patchValue({ deviceId: data['DI'][0], label: data['DL'][0] });
      this.openDialog('existing');
    }
    return true;
  }

  async startScanner(): Promise<void> {
    if (this.cameraRunning() || !this.scannerVideo) {
      return;
    }
    const generation = ++this.scanGeneration;
    this.cameraRunning.set(true);
    this.modalError.set('');
    try {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      if (generation !== this.scanGeneration) {
        return;
      }
      const controls = await new BrowserQRCodeReader().decodeFromVideoDevice(undefined, this.scannerVideo.nativeElement, result => {
        if (result && generation === this.scanGeneration) {
          this.applyQr(result.getText());
        }
      });
      if (generation !== this.scanGeneration) {
        controls.stop();
      } else {
        this.scanner = controls;
      }
    } catch {
      if (generation === this.scanGeneration) {
        this.cameraRunning.set(false);
        this.modalError.set('Camera access is unavailable. Allow camera permission or select a QR image.');
      }
    }
  }

  stopScanner(): void {
    this.scanGeneration++;
    this.scanner?.stop();
    this.scanner = undefined;
    this.cameraRunning.set(false);
  }

  async scanImage(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const generation = this.scanGeneration;
    const url = URL.createObjectURL(file);
    try {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      const result = await new BrowserQRCodeReader().decodeFromImageUrl(url);
      if (generation === this.scanGeneration && this.dialog() === 'scan') {
        this.applyQr(result.getText());
      }
    } catch {
      this.modalError.set('No valid QR code was found in this image.');
    } finally {
      URL.revokeObjectURL(url);
      input.value = '';
    }
  }

  private async submit(action: () => Promise<void>): Promise<void> {
    if (this.busy()) {
      return;
    }
    this.busy.set(true);
    this.modalError.set('');
    this.modalMessage.set('');
    try {
      await action();
    } catch (error) {
      if (!this.handleUnauthorized(error)) {
        this.modalError.set(this.errorMessage(error, 'Unable to complete the request. Please try again.'));
      }
    } finally {
      this.busy.set(false);
    }
  }

  private handleUnauthorized(error: unknown): boolean {
    if ((error as { status?: number })?.status !== 401 || this.destroyRef.destroyed) {
      return false;
    }
    if (this.dialog() !== 'reauth' || !this.modal) {
      this.busy.set(false);
      this.reauthForm.reset({ username: this.auth.currentUserValue?.username || '', password: '' });
      this.openDialog('reauth');
    }
    return true;
  }

  async reauthenticate(): Promise<void> {
    this.reauthForm.markAllAsTouched();
    if (this.busy() || this.reauthForm.invalid) {
      return;
    }
    this.busy.set(true);
    this.modalError.set('');
    try {
      const values = this.reauthForm.getRawValue();
      await this.request(this.auth.login(values.username!.trim(), values.password!, new HttpContext().set(DEVICE_REQUEST_CONTEXT, true)));
      this.busy.set(false);
      this.closeDialog();
      this.toastr.success('Session restored.');
      await this.fetchDevices();
      await this.loadProfile();
    } catch (error) {
      this.modalError.set(this.errorMessage(error, 'Unable to sign in. Please try again.'));
    } finally {
      this.busy.set(false);
    }
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string') {
      return error;
    }
    const response = error as { error?: { message?: string }; message?: string };
    return response?.error?.message || response?.message || fallback;
  }
}
